import React from 'react';
import PropTypes from 'prop-types';
import URI from 'urijs';
import classnames from 'classnames';
import _ from 'underscore';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import {
  Label,
  Select,
} from '@bingads-webui-react/primitive-unthemed';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { Row, Col } from '@bingads-webui-react/layout';

const appendPath = (baseUrl, path) => URI(baseUrl).path(URI.joinPaths(baseUrl, path)).toString();

// return empty string if no FirstName and no LastName
const getFirstNameAndLastName = (user) => {
  const { FirstName, LastName } = user;
  return _.compact([FirstName, LastName]).join(' ');
};

// return empty string if FullName and Name (username) are not different
const getFullNameIfDifferentFromName = (user) => {
  const { FullName, Name } = user;
  return FullName && FullName !== Name ? FullName : '';
};

// return empty string if no FirstName or LastName and FullName equal Name (username)
const formatRealName = (user = {}) =>
  getFirstNameAndLastName(user) || getFullNameIfDifferentFromName(user);

export class CustomMenuBaseInternal extends React.PureComponent {
  constructor(props) {
    super(props);
    const { preferenceService, isDarkThemeEnabled } = this.props;
    if (isDarkThemeEnabled) {
      this.state = {
        themeSelection: preferenceService.findByName('themePreference').Data,
      };
      this.themes = [{ theme: 'Light', value: 'light' }, { theme: 'Dark', value: 'dark' },
        { theme: 'System Preference', value: 'system' }];
      this.handleInputChange = this.handleInputChange.bind(this);
    }
  }

  handleInputChange(event) {
    // FIXME: Needs to be re-implemented to be able to switch .less files without refresh
    this.props.preferenceService.setAtUserLevel({
      Name: 'themePreference',
      Data: event.target.value,
    }).then(() => (
      setTimeout(() => {
        window.location.reload();
      }, 0)
    ));
  }

  render() {
    const {
      classes,
      avatarSrc,
      isMultiUser,
      user,
      customerService,
      i18n,
      customerUrlUpdater,
      url,
      ccBaseUrl,
      signOutUrl: signOutUrlProp,
      supportUrl,
      closePanel,
      panelVisible,
      renderLink: RenderLink,
      fallbackLinkFlags,
      hideCustomerSelector,
      hideCustomerManagement,
      perfMarker,
      CustomerSelector,
      isDarkThemeEnabled,
    } = this.props;

    const isUserInternal = user.IsInternal;
    const customerManagementUrl = appendPath(ccBaseUrl, '/cc/Customers/Management');
    const aggregatorWelcomeUrl = appendPath(ccBaseUrl, '/cc/Welcome');
    const signOutUrl = signOutUrlProp || appendPath(ccBaseUrl, '/signout.aspx');
    const realName = formatRealName(user);
    const closeIconKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        closePanel();
      }
    };

    return (
      <div className={classnames(classes.dropdownContainer, 'dropdown-menu', 'react-dropdown-menu')} role="dialog" aria-labelledby="user-access">
        <FocusLock isControlledFocusLock isFocusLock={panelVisible} focusFirstItemOnMount>
          <div
            className={classes.dropdownTitleContainer}
            id="user-access"
            role="heading"
            aria-level={3}
          >
            {i18n.getString(_TL_('My user access'))}
          </div>
          <div className={classnames('profile-card', classes.profileCard)}>
            <img src={avatarSrc} className={classes.avatarInProfileCard} alt="" />
            <div className={classes.profileCardRightSection}>
              {realName && <div className="real-name">{realName}</div>}
              {user.Name && <div className="user-name">{user.Name}</div>}
              {isDarkThemeEnabled &&
                <Row>
                  <Col sm={4}>
                    <Label>Theme</Label>
                  </Col>
                  <Col sm={8}>
                    <Select onChange={this.handleInputChange}>
                      {this.themes.map(obj => (
                        obj.value === this.state.themeSelection ?
                          <option selected value={obj.value}>{obj.theme}</option> :
                          <option value={obj.value}>{obj.theme}</option>
                      ))}
                    </Select>
                  </Col>
                </Row>}
              {isMultiUser && !hideCustomerManagement &&
                <RenderLink
                  href={customerManagementUrl}
                  className={classnames('customer-management-link', classes.profileCardLink)}
                  isFallback={fallbackLinkFlags.customerManagement}
                  placement="left"
                >
                  {i18n.getString(_TL_('Manage my user access'))}
                </RenderLink>}
              {user.IsAggregator &&
                <RenderLink
                  href={aggregatorWelcomeUrl}
                  className={classnames('aggregator-welcome-link', classes.profileCardLink)}
                  isFallback={fallbackLinkFlags.aggregatorWelcome}
                  placement="left"
                >
                  {i18n.getString(_TL_('View all manager accounts'))}
                </RenderLink>}
              {isUserInternal &&
                <RenderLink
                  href={supportUrl}
                  className={classnames('support-link', classes.profileCardLink)}
                  isFallback={fallbackLinkFlags.supportDashboard}
                  placement="left"
                >
                  {i18n.getString(_TL_('Support'))}
                </RenderLink>}
              <a href={signOutUrl} className={classnames('signout-link', classes.profileCardLink)}>{i18n.getString(_TL_('Sign out'))}</a>
            </div>
          </div>
          {isMultiUser && !hideCustomerSelector && !_.isEmpty(user.CurrentCustomer) &&
          <div className={classnames(classes.customerSelectorContainer, 'customer-selector-container')}>
            <CustomerSelector
              customerId={user.CurrentCustomer.Id}
              customerListService={customerService}
              i18n={i18n}
              url={url}
              customerUrlUpdater={customerUrlUpdater}
              isViewAllCustomerShowAsFallback={fallbackLinkFlags.viewAllCustomers}
              renderLink={RenderLink}
              perfMarker={perfMarker}
            />
          </div>}
          <Glyphicon
            glyph="ba-Cancel"
            className={classes.dropdownCloseButton}
            onClick={closePanel}
            onKeyDown={closeIconKeydown}
            tabIndex={0}
            role="button"
            aria-label={i18n.getString(_TL_('Close the panel'))}
          />
        </FocusLock>
      </div>
    );
  }
}

CustomMenuBaseInternal.propTypes = {
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }),
  user: PropTypes.shape({
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    IsAggregator: PropTypes.bool.isRequired,
    Name: PropTypes.string.isRequired,
    FullName: PropTypes.string.isRequired,
    UserRole: PropTypes.string.isRequired,
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      Name: PropTypes.string,
    }),
  }).isRequired,
  classes: PropTypes.shape({
    dropdownContainer: PropTypes.string,
    avatarInProfileCard: PropTypes.string,
    avatarInToggleButton: PropTypes.string,
    dropdownTitleContainer: PropTypes.string,
    customerSelectorContainer: PropTypes.string,
    profileCard: PropTypes.string,
    profileCardRightSection: PropTypes.string,
    profileCardLink: PropTypes.string,
    dropdownCloseButton: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  customerService: PropTypes.shape({
    getCustomers: PropTypes.func.isRequired,
  }).isRequired,
  ccBaseUrl: PropTypes.string.isRequired,
  supportUrl: PropTypes.string.isRequired,
  customerUrlUpdater: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  avatarSrc: PropTypes.string.isRequired,
  isMultiUser: PropTypes.bool.isRequired,
  closePanel: PropTypes.func.isRequired,
  panelVisible: PropTypes.bool.isRequired,
  fallbackLinkFlags: PropTypes.shape({}).isRequired,
  renderLink: PropTypes.func.isRequired,
  hideCustomerSelector: PropTypes.bool.isRequired,
  CustomerSelector: PropTypes.elementType.isRequired,
  perfMarker: perfMarkerPropTypes,
  signOutUrl: PropTypes.string,
  isDarkThemeEnabled: PropTypes.bool,
  hideCustomerManagement: PropTypes.bool,
};

CustomMenuBaseInternal.defaultProps = {
  perfMarker: null,
  signOutUrl: null,
  preferenceService: null,
  isDarkThemeEnabled: false,
  hideCustomerManagement: false,
};

export const CustomMenuBase = (CustomMenuBaseInternal);
